import React, { type SyntheticEvent, useCallback, useEffect, useMemo, useRef } from 'react';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import type { Placement } from '@atlaskit/popper';
import { type TriggerProps } from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { PopupTrigger } from '@atlassian/entry-point-popup';

import PopupComponent from '../../../../common/no-background-popup-component';
import { getSiteId } from '../../../../common/utils/ari';
import { usePrincipalUser } from '../../../../controllers/principal-user';
import {
	type ExternalShowFlagFn,
	TeamSelectorProvider,
} from '../../../../controllers/team-selector-context';
import { asyncTeamSelectorEntryPoint } from '../../../team-selector/asyncTeamSelector.entrypoint';
import TeamSelectorSkeleton from '../../../team-selector/team-selector-skeleton';
import { type TeamData } from '../../../team-selector/types';
import { getContainerType } from '../../../team-selector/utils/getContainerType';
import { TeamButtonTrigger } from '../team-button-trigger';

export type RenderTriggerProps = {
	triggerProps: TriggerProps;
	onClick: () => void;
};

type TeamButtonComponentProps = {
	containerId: string;
	orgId?: string;
	Wrapper?: React.ElementType;
	numberOfTeams?: number;
	userId?: string;
	onUpdateSuccess?: (params: { teamsConnected: TeamData[]; teamsDisconnected: TeamData[] }) => void;
	/**
	 * Optional placement for the teams popup. Defaults to "bottom-start".
	 * Use "right-start" when rendering inside a side menu to open the panel to the right.
	 */
	placement?: Placement;
	/**
	 * Optional custom trigger renderer. When provided, replaces the default TeamButtonTrigger.
	 * Receives triggerProps (ref, aria-* attributes for popup) and onClick (analytics handler).
	 * The consumer is responsible for rendering an interactive element that spreads triggerProps
	 * and calls onClick on user interaction.
	 */
	renderTrigger?: (props: RenderTriggerProps) => React.ReactNode;
	/**
	 * Optional external flag function. When provided, team connection/disconnection flags
	 * will be shown through this function instead of the component's own FlagsProvider.
	 * This allows the host app to manage all flags in a single stack.
	 */
	showFlag?: ExternalShowFlagFn;
};

export const TeamButtonComponentContent = ({
	numberOfTeams,
	containerId,
	Wrapper = React.Fragment,
	userId,
	renderTrigger,
	placement = 'bottom-start',
}: TeamButtonComponentProps): React.JSX.Element | null => {
	const { fireEvent } = useAnalyticsEvents();
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const [, { setPrincipalUserId }] = usePrincipalUser();

	useEffect(() => {
		if (userId) {
			setPrincipalUserId(userId);
		}
	}, [userId, setPrincipalUserId]);

	const handleButtonClick = useCallback(
		(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			const element = event.currentTarget as HTMLElement;
			const isExpanded = element.getAttribute('aria-expanded') === 'true';
			if (isExpanded) {
				const result = analyticsEvent?.context.find(
					(item) => item.attributes && item.attributes.scope !== undefined,
				);

				fireEvent('ui.teamButton.clicked', {
					teamsCount: numberOfTeams ?? 0,
					scope: result?.attributes?.scope ?? '',
					version: 'm2',
				});
			}
		},
		[fireEvent, numberOfTeams],
	);

	const getTriggerProps = useCallback((triggerProps: TriggerProps) => {
		const setRef = (node: HTMLButtonElement | null) => {
			triggerRef.current = node;
			if (typeof triggerProps.ref === 'function') {
				triggerProps.ref(node);
			} else if (triggerProps.ref) {
				(triggerProps.ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
			}
		};
		return { ...triggerProps, ref: setRef };
	}, []);

	const onFocusTrigger = useCallback(() => {
		setTimeout(() => {
			if (triggerRef.current) {
				triggerRef.current.focus();
			}
		}, 0);
	}, [triggerRef]);

	const entryPointPropsMemoized = useMemo(
		() => ({
			// Remove as part of M1 FG cleanup
			onModalCloseComplete: onFocusTrigger,
		}),
		[onFocusTrigger],
	);

	const handleRenderTriggerClick = useCallback(() => {
		fireEvent('ui.teamButton.clicked', {
			teamsCount: numberOfTeams ?? 0,
			scope: '',
			version: 'm2',
		});
	}, [fireEvent, numberOfTeams]);

	const triggerMemoized = useCallback(
		(triggerProps: TriggerProps) => {
			const processedTriggerProps = getTriggerProps(triggerProps);

			if (renderTrigger && fg('twcg-451-swap-team-button-and-add-people-menu-gate')) {
				return (
					<Wrapper>
						{renderTrigger({
							triggerProps: processedTriggerProps,
							onClick: handleRenderTriggerClick,
						})}
					</Wrapper>
				);
			}

			return (
				<Wrapper>
					<TeamButtonTrigger
						triggerProps={processedTriggerProps}
						// numberOfTeams will never be undefined/0 as we check for the below.
						numberOfTeams={numberOfTeams ?? 0}
						onClick={handleButtonClick}
					/>
				</Wrapper>
			);
		},
		[
			Wrapper,
			getTriggerProps,
			numberOfTeams,
			handleButtonClick,
			renderTrigger,
			handleRenderTriggerClick,
		],
	);

	if (numberOfTeams === undefined) {
		return null;
	}

	return (
		<FlagsProvider>
			<ModalContextProvider>
				<PopupTrigger
					entryPoint={asyncTeamSelectorEntryPoint}
					entryPointProps={entryPointPropsMemoized}
					entryPointParams={{
						containerId,
						cloudId: getSiteId(containerId, { enableLogging: false }) || '',
					}}
					trigger={triggerMemoized}
					fallback={<TeamSelectorSkeleton />}
					placement={placement}
					strategy="fixed"
					popupComponent={PopupComponent}
				/>
			</ModalContextProvider>
		</FlagsProvider>
	);
};

export const TeamButtonComponent = (props: TeamButtonComponentProps): React.JSX.Element => {
	const { containerId, orgId, onUpdateSuccess, showFlag } = props;
	const containerType = getContainerType(containerId);

	return (
		<TeamSelectorProvider
			containerId={containerId}
			containerType={containerType}
			cloudId={getSiteId(containerId) || ''}
			orgId={orgId || ''}
			onUpdateSuccess={onUpdateSuccess}
			showFlag={showFlag}
		>
			<TeamButtonComponentContent {...props} />
		</TeamSelectorProvider>
	);
};
